﻿import { FeMapPlugin, RunwayStripeConfig, RunwayStripesLayer } from '@fe/maplibre-extensions';
import { CustomLayerInterface, ExpressionSpecification, FilterSpecification, Map as MaplibreMap } from 'maplibre-gl';
import app from '../client/app';
import { AirportMetarInfo, AirportRvrInfo, MessageType } from '../services/actor-messages';
import { AirportRunwayData } from '@fe/flight-layer';
import { UDViewAirportSettingsModel } from '../udView/udview';
import { parseColor } from '@fe/core';
import { EventBus } from '../app/contexts/event-bus';
import { computeRunwayOBBs, RunwayOBB, matchesAirport, getRunwayIds } from '../layers/runway-geometry';
import { RunwayLabelsLayer } from '../layers/runway-labels-layer';
import { RunwayWindLayer } from '../layers/runway-wind-layer';
import { RunwayRvrLayer } from '../layers/runway-rvr-layer';

const STRIPES_LAYER_ID = 'fe-runway-stripes-layer';
const BEFORE_LAYER_ID = 'runway-center-arrow-left';

export class RunwayPlugin implements FeMapPlugin {
    private map: MaplibreMap | null = null;
    private runwayStripes = new RunwayStripesLayer(STRIPES_LAYER_ID);
    private runwayLabels = new RunwayLabelsLayer();
    private runwayWind = new RunwayWindLayer();
    private runwayRvr = new RunwayRvrLayer();
    private airportRunwayUsageMap: Map<string, Omit<AirportRunwayData, 'recordId'>> = new Map();
    private metarMap: Map<string, AirportMetarInfo> = new Map();
    private rvrMap: Map<string, AirportRvrInfo> = new Map();
    private lastStripesKey: string = '';
    private lastLabelsKey: string = '';
    private lastWindKey: string = '';
    private lastRvrKey: string = '';

    private handleAirportConfigUpdate = (data: AirportRunwayData) => {
        this.updateRunwayDepictions([data]);
    };

    private handleMetarUpdate = (data: AirportMetarInfo) => {
        this.metarMap.set(data.recordId, data);
    };

    private handleRvrUpdate = (data: AirportRvrInfo) => {
        this.rvrMap.set(data.recordId, data);
    };

    onAdd = (map: MaplibreMap) => {
        this.map = map;

        map.addLayer(this.runwayStripes satisfies CustomLayerInterface, BEFORE_LAYER_ID);
        this.runwayLabels.onAdd(map, BEFORE_LAYER_ID);
        this.runwayWind.onAdd(map, BEFORE_LAYER_ID);
        this.runwayRvr.onAdd(map, BEFORE_LAYER_ID);

        EventBus.on('airport-config-updated', this.handleAirportConfigUpdate);
        EventBus.on('airport-metar-updated', this.handleMetarUpdate);
        EventBus.on('airport-rvr-updated', this.handleRvrUpdate);

        const provideRunwayUsage = async () => {
            const airportConfigs = await app.worker.actor.sendAsync({ type: MessageType.getRunwayAirportConfigs });
            if (airportConfigs) {
                this.updateRunwayDepictions(airportConfigs);
            }
        };

        const provideMetarData = async () => {
            const metars = await app.worker.actor.sendAsync({ type: MessageType.getAirportMetars });
            if (metars) {
                metars.forEach((m) => this.metarMap.set(m.recordId, m));
            }
        };

        const provideRvrData = async () => {
            const rvrs = await app.worker.actor.sendAsync({ type: MessageType.getAirportRvrs });
            if (rvrs) {
                rvrs.forEach((r) => this.rvrMap.set(r.recordId, r));
            }
        };

        void provideRunwayUsage();
        void provideMetarData();
        void provideRvrData();
    };

    onRemove = () => {
        if (this.map) {
            if (this.map.getLayer(STRIPES_LAYER_ID)) {
                this.map.removeLayer(STRIPES_LAYER_ID);
            }
            this.runwayLabels.onRemove(this.map);
            this.runwayWind.onRemove(this.map);
            this.runwayRvr.onRemove(this.map);
        }

        this.airportRunwayUsageMap.clear();
        this.metarMap.clear();
        this.rvrMap.clear();
        this.map = null;

        EventBus.off('airport-config-updated', this.handleAirportConfigUpdate);
        EventBus.off('airport-metar-updated', this.handleMetarUpdate);
        EventBus.off('airport-rvr-updated', this.handleRvrUpdate);
    };

    onIdle = (settings: UDViewAirportSettingsModel) => {
        if (!this.map || !settings) {
            return;
        }
        this.updateAll(settings);
    };

    changeSettings = (settings: UDViewAirportSettingsModel) => {
        this.updateAll(settings);
        this.updateDirectionFilters();
    };

    private updateAll = (settings: UDViewAirportSettingsModel) => {
        if (!this.map || !settings) {
            return;
        }

        // Compute geometry once, share across all features
        const obbs = computeRunwayOBBs(this.map);

        this.updateStripes(settings, obbs);
        this.updateLabels(settings, obbs);
        this.updateWindIndicators(settings, obbs);
        this.updateRvrIndicators(settings, obbs);
    };

    private updateStripes = (settings: UDViewAirportSettingsModel, obbs: RunwayOBB[]) => {
        if (!settings.activeRunwayDepiction) {
            this.runwayStripes.setRunways([]);
            this.lastStripesKey = '';
            return;
        }

        const runwayConfigs: RunwayStripeConfig[] = [];

        for (const airport of this.airportRunwayUsageMap.values()) {
            const matchingObbs = obbs.filter((obb) => {
                if (!matchesAirport(obb, airport)) {
                    return false;
                }
                const runwayIds = getRunwayIds(obb);
                return (
                    airport.arrivalRunways.some((r) => runwayIds.includes(r)) ||
                    airport.departureRunways.some((r) => runwayIds.includes(r))
                );
            });

            for (const obb of matchingObbs) {
                const runwayIds = getRunwayIds(obb);
                const isDeparture = airport.departureRunways.some((r) => runwayIds.includes(r));
                const isArrival = airport.arrivalRunways.some((r) => runwayIds.includes(r));

                runwayConfigs.push({
                    corners: obb.corners,
                    mode: isArrival && isDeparture ? 'both' : isArrival ? 'arrival' : 'departure',
                    arriveColor: parseColor(settings.arrivalColor).slice(0, 3) as [number, number, number],
                    departColor: parseColor(settings.departureColor).slice(0, 3) as [number, number, number]
                });
            }
        }

        const key = JSON.stringify(runwayConfigs);
        if (key !== this.lastStripesKey) {
            this.lastStripesKey = key;
            this.runwayStripes.setRunways(runwayConfigs);
        }
    };

    private updateLabels = (settings: UDViewAirportSettingsModel, obbs: RunwayOBB[]) => {
        if (!settings.runwayLabels) {
            this.runwayLabels.clear();
            this.lastLabelsKey = '';
            return;
        }

        // Show labels for ALL visible runways (deduplicate by featureId)
        const seen = new Set<string | number>();
        const deduped = obbs.filter((obb) => {
            if (obb.featureId == null) {
                return true;
            }
            if (seen.has(obb.featureId)) {
                return false;
            }
            seen.add(obb.featureId);
            return true;
        });

        const key = JSON.stringify(deduped.map((o) => [o.featureId, o.endA, o.endB, o.bearingDeg]));
        if (key !== this.lastLabelsKey) {
            this.lastLabelsKey = key;
            this.runwayLabels.update(deduped);
        }
    };

    private updateWindIndicators = (settings: UDViewAirportSettingsModel, obbs: RunwayOBB[]) => {
        if (!settings.runwayWindIndicators) {
            this.runwayWind.clear();
            this.lastWindKey = '';
            return;
        }

        const key = JSON.stringify({
            obbs: obbs.map((o) => [o.featureId, o.center, o.bearingDeg]),
            metars: Array.from(this.metarMap.entries()),
            gust: { useGust: settings.runwayWindGust, gustFactor: settings.runwayWindGustFactor }
        });

        if (key !== this.lastWindKey) {
            this.lastWindKey = key;
            this.runwayWind.update(obbs, this.metarMap, {
                useGust: settings.runwayWindGust,
                gustFactor: settings.runwayWindGustFactor
            });
        }
    };


    private updateRvrIndicators = (settings: UDViewAirportSettingsModel, obbs: RunwayOBB[]) => {
        if (!settings.runwayRvrIndicators) {
            this.runwayRvr.clear();
            this.lastRvrKey = '';
            return;
        }

        const key = JSON.stringify({
            obbs: obbs.map((o) => [o.featureId, o.corners, o.bearingDeg]),
            rvrs: Array.from(this.rvrMap.entries())
        });

        if (key !== this.lastRvrKey) {
            this.lastRvrKey = key;
            this.runwayRvr.update(obbs, this.rvrMap);
        }
    };


    private updateRunwayUsageData(airportConfig: AirportRunwayData): void {
        const { recordId, ...airportData } = airportConfig;
        // there is other stuff in the runway data string, but we are only interested in runway ids
        airportData.departureRunways = airportData.departureRunways.map((x) => {
            const match = x.match(/\b(\d{2}[LRC]?)\b/);
            return match ? match[1] : x;
        });
        airportData.arrivalRunways = airportData.arrivalRunways.map((x) => {
            const match = x.match(/\b(\d{2}[LRC]?)\b/);
            return match ? match[1] : x;
        });
        this.airportRunwayUsageMap.set(recordId, airportData);
    }

    private updateRunwayDepictions(airportConfigs: AirportRunwayData[]): void {
        airportConfigs.forEach((airportConfig) => {
            this.updateRunwayUsageData(airportConfig);
        });

        this.updateDirectionFilters();
    }

    private updateDirectionFilters(): void {
        let arrowLeftFilter: FilterSpecification = false; // recip to base
        let arrowRightFilter: FilterSpecification = false; // base to recip
        for (const value of this.airportRunwayUsageMap.values()) {
            const airportMatch: ExpressionSpecification = [
                'any',
                value.icaoId ? ['==', ['get', 'icaoid'], value.icaoId] : false,
                value.iataId ? ['==', ['get', 'iataid'], value.iataId] : false,
                value.faaId ? ['==', ['get', 'faaid'], value.faaId] : false
            ];

            // Server sometimes sends duplicate runway ids, so we need to deduplicate them before creating filters to prevent catastrophic app failure
            const departureRunways = [...new Set(value.departureRunways)];
            if (departureRunways.length > 0) {
                arrowRightFilter = [
                    'any',
                    arrowRightFilter,
                    ['all', airportMatch, ['match', ['get', 'baseid'], departureRunways, true, false]]
                ];
                arrowLeftFilter = [
                    'any',
                    arrowLeftFilter,
                    ['all', airportMatch, ['match', ['get', 'recipid'], departureRunways, true, false]]
                ];
            }

            const arrivalRunways = [...new Set(value.arrivalRunways)];
            if (arrivalRunways.length > 0) {
                arrowRightFilter = [
                    'any',
                    arrowRightFilter,
                    ['all', airportMatch, ['match', ['get', 'baseid'], arrivalRunways, true, false]]
                ];
                arrowLeftFilter = [
                    'any',
                    arrowLeftFilter,
                    ['all', airportMatch, ['match', ['get', 'recipid'], arrivalRunways, true, false]]
                ];
            }
        }
        this.setFilter('runway-center-arrow-left', arrowLeftFilter);
        this.setFilter('runway-center-arrow-right', arrowRightFilter);
    }

    private setFilter(layerId: string, filter: FilterSpecification): void {
        if (!this.map || !this.map.getLayer(layerId)) {
            console.warn(`Layer ${layerId} not found`);
            return;
        }

        this.map.setFilter(layerId, filter);
    }
}
